import { Empty } from "google-protobuf/google/protobuf/empty_pb"
import {
    ReactElement,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { take } from "rxjs/operators"
import { UserSetting } from "../grpc"
import useAuthGRPC from "../hooks/useAuthGRPC"
import { AuthContext } from "./auth"

interface IShareLinkSettingContext {
    shareLinkEncrypted: boolean
    setShareLinkEncrypted: (value: boolean) => void
}

export const ShareLinkSettingContext = createContext<IShareLinkSettingContext>({
    shareLinkEncrypted: false,
    setShareLinkEncrypted: () => {},
})

ShareLinkSettingContext.displayName = "ShareLinkSettingContext"

export function ShareLinkSettingProvider({
    children,
}: {
    children: ReactElement
}) {
    const { init: initialized, login } = useContext(AuthContext)
    const queryShareLinkSetting = useAuthGRPC(
        UserSetting.queryShareLinkSetting,
        UserSetting,
    )
    const [shareLinkEncrypted, setShareLinkEncryptedState] = useState(false)

    useEffect(() => {
        if (!initialized || !login) {
            setShareLinkEncryptedState(false)
            return
        }

        const sub = queryShareLinkSetting(new Empty())
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    setShareLinkEncryptedState(
                        response.getShareLinkEncrypted?.() ?? false,
                    )
                },
                error: () => {
                    setShareLinkEncryptedState(false)
                },
            })

        return () => sub.unsubscribe()
    }, [initialized, login, queryShareLinkSetting])

    const setShareLinkEncrypted = useCallback((value: boolean) => {
        setShareLinkEncryptedState(value)
    }, [])

    const value = useMemo(
        () => ({
            shareLinkEncrypted,
            setShareLinkEncrypted,
        }),
        [shareLinkEncrypted, setShareLinkEncrypted],
    )

    return (
        <ShareLinkSettingContext.Provider value={value}>
            {children}
        </ShareLinkSettingContext.Provider>
    )
}

export function useShareLinkSettingContext() {
    return useContext(ShareLinkSettingContext)
}
